import { React, useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import lodash from 'lodash';
import Lottie from 'lottie-react';

import NewTimeDivisionLine from '../../../Component/MainComponent/NewTimeDivisionLine.js';
import WeeklyColumn from './WeeklyColumn';

import EmptySign from '../../../image/EmptySign.png';
import PlusPath from '../../../image/ClassAddPlus.png';

import LoadingAnimation from '../../../Animation/LoadingAnimation.json';
import WeekClassDeleteModal from './ClassManageModal/WeekClassDeleteModal.js';
import FetchModule from '../../Share/Network/FetchModule.js';
import ScheduleClassBox from './ScheduleClassBox.js';
import { cleanTeacherName } from '../../Share/Normal/useCoachName.js';
import { parseClassTimeRange } from '../../Share/Normal/useClassTime.js';

// 높이 계산 상수
const HEIGHT_CONSTANTS = {
  WEEKLY_COLUMN_PADDING: 12,
  COLUMN_GAP: 20,
  HEADER_HEIGHT: 28,
  BOTTOM_MARGIN: 7,
  SCHEDULE_BOX_BASE_HEIGHT: 78,  // 스케줄 박스 기본 높이
  SCHEDULE_BOX_GAP: 6,  // 스케줄 박스와 + 박스 사이 갭 (marginTop/marginBottom)
  ROW_GAP: 8,  // 행 간 갭
  HOVER_OFFSET: 2  // 호버 시 테두리 오프셋
};

// 요일 인덱스 배열 (일~토)
const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6];

export default function WeeklyClass(props) {
  const {
    index,
    rendering,
    setRendering,
    today,
    isLoading,
    setIsLoading,
    isInProgress,
    setIsInProgress,
    scheduleData,
    breakTimeData,
    changedTeachers,
    canceledSchedules,
    setClickDate,
    onClickDeleteButton,
    onClickScheduleDetail,
    onClickPlusColumn,
    selectedCoaches = [], // 기본값 설정
    conversionRate = 0.5 // 시간 변환 비율 (props로 받되 기본값 0.5)
  } = props;

  const [teachersData, setTeachersData] = useState([]);
  const [lessonsData, setLessonsData] = useState([]);
  const [startDay, setStartDay] = useState(moment());
  const [minHeight, setMinHeight] = useState(644)
  const [isClickedWeekDelete, setIsClickedWeekDelete] = useState(false);
  const [deletedWeek, setDeletedWeek] = useState(undefined);

  const [weekAllDeleteBackgroundHeight, setWeekAllDeleteBackgroundHeight] = useState({ width: 0, height: 0 });

  const TopLevelRef = useRef();
  const ColumnRef = useRef();
  const ComponentRef = useRef([]);
  const FirstButtonWrapperRef = useRef([]);
  const FirstButtonRef = useRef([]);
  const ButtonWrapperRef = useRef([]);
  const ButtonRef = useRef([]);

  // teachers/lessons Map 인덱스 생성 //
  const lessonsMap = useMemo(() => {
    const map = new Map();

    lessonsData.forEach(lesson => {
      map.set(lesson.id, lesson);
    });

    return map;
  }, [lessonsData]);

  const teachersMap = useMemo(() => {
    const map = new Map();

    teachersData.forEach(teacher => {
      map.set(teacher.name, teacher);
    });

    return map;
  }, [teachersData]);

  useEffect(() => {
    if (!ColumnRef.current || isLoading) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const rect = entry.target.getBoundingClientRect();

        setWeekAllDeleteBackgroundHeight({
          width: rect.width,
          height: rect.height
            + HEIGHT_CONSTANTS.WEEKLY_COLUMN_PADDING
            + HEIGHT_CONSTANTS.COLUMN_GAP
            + HEIGHT_CONSTANTS.HEADER_HEIGHT
            + HEIGHT_CONSTANTS.BOTTOM_MARGIN
        });
      }
    });

    resizeObserver.observe(ColumnRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isLoading]);

  // Teachers 및 Lessons 데이터 가져오기 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('lesson/zone/schedule', 'GET');

        if (responseData.status === 200) {
          setTeachersData(responseData.data.teachers || []);
          setLessonsData(responseData.data.lessons || []);
        }
        else {
          alert(responseData.error);
        }
      }
    )();
  }, []);

  // 주간 처음 날짜 빼오기 //
  useEffect(() => {
    for (let i = 0; i < 7; i++) {
      if (today.clone().subtract(i, 'day').format('dd') === 'Su') {
        setStartDay(today.clone().subtract(i, 'day'));
        break;
      }
    }
  }, [today])

  // Schedule Data를 사용 가능한 Data로 가공
  const useScheduleData = useMemo(() => {
    if (scheduleData.length === 0 && breakTimeData.length === 0) {
      return [];
    }

    const newScheduleData = [];
    let tmp = scheduleData.slice();

    for (let i = 0; i < tmp.length; i++) {
      let endPoint = moment(tmp[i].classDate.split('T')[0]);

      if (endPoint.isBefore(startDay)) endPoint = startDay;

      const repeatWeekSet = new Set(tmp[i].repeatWeek);
      const applyWeekSet = tmp[i].applyWeek === 'All' ? null : new Set(tmp[i].applyWeek.split(','));

      while (endPoint.isBefore(moment(tmp[i].endDate))) {
        if (endPoint.diff(startDay, 'days') >= 7) break;

        if (repeatWeekSet.has(getWeekNumber(endPoint.format('YYYY-MM-DD')).toString()) || repeatWeekSet.has('0')) {
          if (applyWeekSet === null || applyWeekSet.has(endPoint.format('ddd'))) {

            // lessonId로 lesson 찾고, 모든 teacher의 정보 가져오기
            let teacherProfile = '';
            let teacherProfiles = [];
            let teacherNames = [];
            let displayTeacherName = '';

            if (tmp[i].lessonId && lessonsMap.size > 0 && teachersMap.size > 0) {
              const lesson = lessonsMap.get(tmp[i].lessonId);

              if (lesson && lesson.teachers && lesson.teachers.length > 0) {
                // lesson에서 가져온 teachers 배열 (이름 배열)
                teacherNames = lesson.teachers;

                // 각 teacher의 profile 가져오기 (최대 4명)
                for (let t = 0; t < Math.min(teacherNames.length, 4); t++) {
                  const teacher = teachersMap.get(teacherNames[t]);

                  teacherProfiles.push(teacher?.profile || '');
                }

                // 첫 번째 teacher profile (기존 호환성 유지)
                if (teacherProfiles.length > 0) {
                  teacherProfile = teacherProfiles[0];
                }

                // 표시용 teacherName 생성
                if (teacherNames.length > 1) {
                  const firstName = cleanTeacherName(teacherNames[0]);

                  displayTeacherName = `${firstName} 외 ${teacherNames.length - 1}명`;
                }
                else if (teacherNames.length === 1) {
                  displayTeacherName = cleanTeacherName(teacherNames[0]);
                }
              }
            }

            // teacherName 백업 (lesson에서 못 가져온 경우)
            if (!displayTeacherName && tmp[i].teacherName) {
              displayTeacherName = cleanTeacherName(tmp[i].teacherName);
            }

            const newData = {
              classId: tmp[i].classId,
              classDate: endPoint.format('YYYY.MM.DD'),
              className: tmp[i].className,
              classTime: tmp[i].classTime,
              color: tmp[i].color,
              endDate: tmp[i].endDate,
              lessonId: tmp[i].lessonId,
              scheduleId: tmp[i].scheduleId,
              teacherName: displayTeacherName,
              teacherNames: teacherNames,
              teacherProfile: teacherProfile,
              teacherProfiles: teacherProfiles,
              applyWeek: tmp[i].applyWeek,
              repeatWeek: tmp[i].repeatWeek,
              maxBookedPeople: tmp[i].maxBookedPeople,
              deduction: tmp[i].deduction,
            }

            newScheduleData.push(newData);
          }
        }

        endPoint = endPoint.clone().add(1, 'day');
      }
    }

    newScheduleData.sort(function (a, b) {
      return a.classDate < b.classDate ? -1 : a.classDate > b.classDate ? 1 : 0;
    })

    const newData = [];

    for (let i = 0; i < newScheduleData.length; i++) {
      for (let j = 0; j < 7; j++) {
        if (newScheduleData[i].classDate === startDay.clone().add(j, 'day').format('YYYY.MM.DD')) {
          newData.push(newScheduleData[i]);
        }
      }
    }

    // breaktime 데이터 가공
    for (let z = 0; z < breakTimeData.length; z++) {
      const breakRepeatWeekSet = new Set(breakTimeData[z].repeatWeek);
      const breakApplyWeekSet = breakTimeData[z].applyWeek === 'All' ? null : new Set(breakTimeData[z].applyWeek.split(','));

      for (let y = 0; y < 7; y++) {
        if (moment(breakTimeData[z].classDate).isBefore(moment(startDay.clone().add(y, 'day').format('YYYY-MM-DDT') + breakTimeData[z].startTime)) && moment(breakTimeData[z].endDate).isAfter(startDay.clone().add(y, 'day'))) {
          const currentDayWeekNum = getWeekNumber(startDay.clone().add(y, 'day').format('YYYY-MM-DD'));
          const currentDayOfWeek = startDay.clone().add(y, 'day').format('ddd');

          if (breakRepeatWeekSet.has(currentDayWeekNum) || breakRepeatWeekSet.has('0')) {
            if (breakApplyWeekSet === null || breakApplyWeekSet.has(currentDayOfWeek)) {
              const breakData = {
                breakTimeId: breakTimeData[z].breakTimeId,
                classId: -1,
                lessonId: -1,
                classDate: startDay.clone().add(y, 'day').format('YYYY.MM.DD'),
                color: '#BDBDBD',
                classTime: breakTimeData[z].startTime + '-' + breakTimeData[z].endTime,
                className: '브레이크 타임',
                applyWeek: breakTimeData[z].applyWeek
              }

              newData.push(breakData);
            }
          }
        }
      }
    }

    newData.sort(function (a, b) {
      return a.classTime < b.classTime ? -1 : a.classTime > b.classTime ? 1 : 0;
    })

    // TimeDivisionLine에서 사용할 데이터 가공
    for (let i = 0; i < newData.length; i++) {
      tmp = newData[i].classTime.split('-');
      let splitTmp = tmp[0].split(':');
      let Time = '';

      if (parseInt(splitTmp[0]) === 24) {
        newData[i].startTime = (parseInt(splitTmp[0]) - 12) + ':' + splitTmp[1] + ' AM';
        Time = (parseInt(splitTmp[0]) - 12) + 'AM';
      }
      else if (parseInt(splitTmp[0]) > 12) {
        newData[i].startTime = (parseInt(splitTmp[0]) - 12) + ':' + splitTmp[1] + ' PM';
        Time = (parseInt(splitTmp[0]) - 12) + ' PM';
      }
      else if (parseInt(splitTmp[0]) === 12) {
        newData[i].startTime = (parseInt(splitTmp[0])) + ':' + splitTmp[1] + ' PM';
        Time = (parseInt(splitTmp[0])) + ' PM';
      }
      else {
        newData[i].startTime = splitTmp[0] + ':' + splitTmp[1] + ' AM';
        Time = parseInt(splitTmp[0]) + ' AM';
      }

      tmp = newData[i].classTime.split('-');
      splitTmp = tmp[1].split(':');

      if (parseInt(splitTmp[0]) === 24) {
        newData[i].endTime = (parseInt(splitTmp[0]) - 12) + ':' + splitTmp[i] + 'AM';
      }
      else if (parseInt(splitTmp[0]) > 12) {
        newData[i].endTime = (parseInt(splitTmp[0]) - 12) + ':' + splitTmp[1] + ' PM';
        // 끝나는 시간 소팅을 위해 쓰는 변수 //
      }
      else if (parseInt(splitTmp[0]) === 12) {
        newData[i].endTime = (parseInt(splitTmp[0])) + ':' + splitTmp[1] + ' PM';
      }
      else {
        newData[i].endTime = splitTmp[0] + ':' + splitTmp[1] + ' AM';
      }

      newData[i].end = parseFloat(splitTmp[0] + '.' + splitTmp[1]);

      // 정각 시작 체크 (분이 00인 경우)
      tmp = newData[i].classTime.split('-');
      const startMinute = tmp[0].split(':')[1];

      // 정각 시작이 아니고, 이전 수업과 hour가 같으면 시간 표시 안 함
      if (i !== 0 && startMinute !== '00' && newData[i - 1].startTime.split(':')[0] === newData[i].startTime.split(':')[0]) {
        newData[i] = Object.assign(newData[i], { isNeedNewDivide: false })
        newData[i] = Object.assign(newData[i], { viewTime: Time })
      }
      else {
        newData[i] = Object.assign(newData[i], { isNeedNewDivide: true })
        newData[i] = Object.assign(newData[i], { viewTime: Time })
      }
    }

    const newReserve = [];

    for (let h = 0; h < newData.length; h++) {
      newReserve.push(new Array());
    }

    return newData;
  }, [scheduleData, breakTimeData, startDay, teachersData, lessonsData, lessonsMap, teachersMap])

  // 주간 시간표 데이터 정리 //
  const totalData = useMemo(() => {
    if (!useScheduleData || useScheduleData.length === 0) {
      return [];
    }

    const tmpData = lodash.cloneDeep(useScheduleData);

    // 원본 데이터의 teacherName에서 "Coach" 접미사 제거
    // 주의: "외 n명" 형식은 이미 cleanTeacherName 처리가 완료된 상태이므로 다시 처리하지 않음
    for (let i = 0; i < tmpData.length; i++) {
      if (tmpData[i].teacherName && !tmpData[i].teacherName.includes('외')) {
        tmpData[i].teacherName = cleanTeacherName(tmpData[i].teacherName);
      }
    }

    const newTotalData = [];
    const startTimeList = [];

    tmpData.sort(function (a, b) {
      return a.classDate < b.classDate ? -1 : a.classDate > b.classDate ? 1 : 0;
    })

    for (let i = 0; i < tmpData.length; i++) {
      const data = {
        classDate: tmpData[i].classDate,
        startTime: tmpData[i].startTime,
        endTime: tmpData[i].endTime
      }

      startTimeList.push(data);
    }

    const seenStartTimes = new Set();
    const finalStartTime = [];

    for (let i = 0; i < startTimeList.length; i++) {
      if (!seenStartTimes.has(startTimeList[i].startTime)) {
        seenStartTimes.add(startTimeList[i].startTime);
        finalStartTime.push(startTimeList[i]);
      }
    }

    finalStartTime.sort(function (a, b) {
      const aTime = a.startTime.split(' ')[0];
      const aState = a.startTime.split(' ')[1];

      const bTime = b.startTime.split(' ')[0];
      const bState = b.startTime.split(' ')[1];

      if (aState === 'AM' && bState === 'PM') return -1;
      else if (aState === 'PM' && bState === 'AM') return 1;
      else {
        const aHour = aTime.split(':')[0];
        const aMinute = aTime.split(':')[1];
        let aConvertToMinute = 0;

        if (aState === 'PM' && parseInt(aHour) === 12) aConvertToMinute = parseInt(aMinute);
        else aConvertToMinute = (parseInt(aHour) * 60) + parseInt(aMinute);

        const bHour = bTime.split(':')[0];
        const bMinute = bTime.split(':')[1];
        let bConvertToMinute = 0;

        if (bState === 'PM' && parseInt(bHour) === 12) bConvertToMinute = parseInt(bMinute);
        else bConvertToMinute = (parseInt(bHour) * 60) + parseInt(bMinute);

        if (aConvertToMinute > bConvertToMinute) return 1;
        else return -1;
      }
    })

    // startTime, endTime별로 데이터 그룹핑 (O(n))
    const byStartTime = new Map();

    for (let j = 0; j < tmpData.length; j++) {
      const item = tmpData[j];
      const itemKey = `${item.classDate}_${item.classTime}_${item.className}`;

      // startTime별 그룹핑
      if (!byStartTime.has(item.startTime)) {
        byStartTime.set(item.startTime, []);
      }

      byStartTime.get(item.startTime).push({ item, key: itemKey });
    }

    const addedToTotal = new Set();

    // Map에서 바로 꺼내서 처리 (O(m))
    for (let i = 0; i < finalStartTime.length; i++) {
      const newDataList = [];
      const addedToList = new Set();

      // startTime이 같은 항목들만 추가 (시작 시간 기준으로만 정렬)
      const startItems = byStartTime.get(finalStartTime[i].startTime) || [];

      for (const { item, key } of startItems) {
        if (!addedToTotal.has(key)) {
          newDataList.push(item);
          addedToList.add(key);
        }
      }

      if (newDataList.length !== 0) {
        newTotalData.push(newDataList);

        // newTotalData에 추가된 항목들을 Set에 기록
        for (const key of addedToList) {
          addedToTotal.add(key);
        }
      }
    }

    // 겹치는 시간표 처리 (최적화: Set/Map 사용으로 O(M³) → O(M log M)) //
    const optimizedTotalData = [];

    for (let i = 0; i < newTotalData.length; i++) {
      const row = newTotalData[i];

      // Set을 사용하여 날짜 중복 체크 - O(row.length)
      const seenDates = new Set();
      let isNeedDivide = false;

      for (const item of row) {
        if (seenDates.has(item.classDate)) {
          isNeedDivide = true;
          break;
        }

        seenDates.add(item.classDate);
      }

      // 데이터의 크기가 일정 이상이거나 시간대별 동일 날짜가 겹치는 경우가 있을때 //
      if (row.length > 7 || isNeedDivide) {
        // Map을 사용하여 날짜별 그룹핑 - O(row.length)
        const dateGroups = new Map();

        for (const item of row) {
          if (!dateGroups.has(item.classDate)) {
            dateGroups.set(item.classDate, []);
          }

          dateGroups.get(item.classDate).push(item);
        }

        // 날짜순 정렬된 키 배열 생성
        const sortedDates = Array.from(dateGroups.keys()).sort();

        // 각 날짜 그룹 내에서 일찍 끝나는 순으로 정렬
        for (const items of dateGroups.values()) {
          items.sort((a, b) => a.end - b.end);
        }

        // 최대 행 수 계산
        let maxRowCount = 0;

        for (const items of dateGroups.values()) {
          if (items.length > maxRowCount) maxRowCount = items.length;
        }

        // 행별로 데이터 재구성
        for (let rowIdx = 0; rowIdx < maxRowCount; rowIdx++) {
          const newRow = [];

          for (const date of sortedDates) {
            const items = dateGroups.get(date);

            if (items.length > rowIdx) {
              newRow.push(items[rowIdx]);
            }
          }

          // isNeedNewDivide 설정
          if (rowIdx !== 0) {
            for (const item of newRow) {
              item.isNeedNewDivide = false;
            }
          }
          else if (i === 0) {
            // 전체 시간표의 첫 번째 행은 항상 시간 구분선 표시
            for (const item of newRow) {
              item.isNeedNewDivide = true;
            }
          }

          optimizedTotalData.push(newRow);
        }
      }
      else {
        optimizedTotalData.push(row);
      }
    }
    // 겹치는 시간표 처리 //

    // 남은 칸 수업없음으로 채우기 (최적화: Map 사용으로 O(M²) → O(M)) //
    for (let i = 0; i < optimizedTotalData.length; i++) {
      const row = optimizedTotalData[i];

      if (row.length === 0) continue;

      // 기존 데이터를 날짜 인덱스로 Map에 저장 - O(row.length)
      const existingByDayIndex = new Map();

      for (const item of row) {
        const dayIndex = moment(item.classDate, 'YYYY.MM.DD').diff(startDay, 'days');

        existingByDayIndex.set(dayIndex, item);
      }

      // 7칸 배열 생성 및 채우기 - O(7)
      const filledRow = [];
      const templateItem = row[0];

      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        if (existingByDayIndex.has(dayIdx)) {
          filledRow.push(existingByDayIndex.get(dayIdx));
        }
        else {
          // 수업없음 셀 생성
          filledRow.push({
            classId: -2,
            classDate: startDay.clone().add(dayIdx, 'day').format('YYYY.MM.DD'),
            className: '수업없음',
            classTime: ' ',
            teacherName: ' ',
            teacherProfile: '',
            isNeedNewDivide: templateItem.isNeedNewDivide,
            viewTime: templateItem.viewTime
          });
        }
      }

      optimizedTotalData[i] = filledRow;
    }

    // 최적화된 데이터를 newTotalData에 반영
    newTotalData.length = 0;
    newTotalData.push(...optimizedTotalData);

    if (changedTeachers.length > 0) {
      const teacherMap = new Map();

      for (const teacher of changedTeachers) {
        const key = `${teacher.scheduleId}_${moment(teacher.classDate).format('YYYY.MM.DD')}`;

        teacherMap.set(key, teacher.teacherNames);
      }

      for (let i = 0; i < newTotalData.length; i++) {
        for (let j = 0; j < newTotalData[i].length; j++) {
          const item = newTotalData[i][j];
          const key = `${item.scheduleId}_${item.classDate}`;
          const teacherNames = teacherMap.get(key);

          if (teacherNames) {
            const names = teacherNames.split(',').map(name => name.trim());

            item.isChangedTeachers = true;
            item.teacherNames = names;

            // 백엔드에서 온 순서대로 첫 번째 선생님 처리
            if (names.length > 0) {
              const firstName = cleanTeacherName(names[0].trim());

              // 여러 명일 때 "첫번째이름 외 n명" 형식으로 표시
              if (names.length > 1) {
                item.teacherName = `${firstName} 외 ${names.length - 1}명`;
              }
              else {
                item.teacherName = firstName;
              }

              // 모든 선생님의 profile 배열 생성 (최대 4명)
              if (teachersMap.size > 0) {
                const profiles = [];

                for (let i = 0; i < Math.min(names.length, 4); i++) {
                  const teacher = teachersMap.get(names[i].trim());

                  profiles.push(teacher?.profile || '');
                }

                item.teacherProfiles = profiles;
                item.teacherProfile = profiles[0]; // 첫 번째는 기존 호환성을 위해 유지
              }
            }
            else {
              item.teacherName = '';
            }
          }
          else {
            // changedTeachers에 없는 경우, 기존 teacherNames 유지
            item.isChangedTeachers = false;
            // item.teacherNames는 useScheduleData에서 만든 값을 그대로 유지
          }
        }
      }
    }

    return newTotalData;

  }, [useScheduleData, changedTeachers, startDay, teachersData, lessonsData, lessonsMap, teachersMap])

  // 선택된 코치로 필터링된 데이터 //
  const filteredTotalData = useMemo(() => {
    // totalData가 없으면 빈 배열 반환
    if (!totalData || totalData.length === 0) {
      return [];
    }

    // 선택된 코치가 없으면 전체 데이터 반환
    if (!Array.isArray(selectedCoaches) || selectedCoaches.length === 0) return totalData;

    const selectedCoachesSet = new Set(selectedCoaches);

    // 선택된 코치로 필터링 (각 row는 항상 7개 요소 유지)
    const filteredRows = totalData.map(row => {
      if (!Array.isArray(row)) return row;

      return row.map(item => {
        // "수업없음"은 항상 표시
        if (item.className === '수업없음') return item;

        // teacherNames 배열 가져오기 (없거나 비어있으면 teacherName으로 대체)
        let teacherNames = [];

        if (item.teacherNames && item.teacherNames.length > 0) {
          teacherNames = item.teacherNames;
        }
        else if (item.teacherName) {
          teacherNames = [item.teacherName];
        }

        // 선택된 코치 중 하나라도 포함되어 있는지 확인
        const shouldShow = teacherNames.some(name => {
          const cleanName = cleanTeacherName(name);

          return selectedCoachesSet.has(cleanName);
        });

        // 표시해야 하면 원본, 아니면 "수업없음"으로 대체
        if (shouldShow) {
          return item;
        }
        else {
          return {
            ...item,
            className: '수업없음'
          };
        }
      });
    });

    // 모든 요일이 "수업없음"인 row는 제거 (시간표 길이 동적 조정)
    const result = filteredRows.filter(row => {
      if (!Array.isArray(row)) return true;

      // row의 7개 요소 중 하나라도 "수업없음"이 아닌 것이 있으면 유지
      return row.some(item => item.className !== '수업없음');
    });

    // 필터링 후에도 첫 번째 행은 항상 타임라인 표시
    result[0]?.forEach(item => {
      item.isNeedNewDivide = true;
    });

    return result;
  }, [totalData, selectedCoaches]);

  // totalData 계산 완료 후 isLoading 해제 //
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [totalData, setIsLoading]);

  useEffect(() => {
    if (!isLoading) {
      setMinHeight(ColumnRef.current.clientHeight);
    }
  }, [isLoading, ColumnRef])

  // 병합 정보 계산 //
  const mergeInfo = useMemo(() => {
    if (filteredTotalData.length > 0) {
      return calculateMergedCells(filteredTotalData);
    }
    return {};
  }, [filteredTotalData])

  // 몇주차인지 계산하기 //
  function getWeekNumber(date) {
    const standard = new Date(date);
    const currentDate = standard.getDate();
    const startOfMonth = new Date(standard.setDate(1));
    const weekDay = startOfMonth.getDay();

    return parseInt(((weekDay - 1) + currentDate) / 7) + 1;
  }

  // 수업없음 셀 병합 정보 계산 //
  function calculateMergedCells(data) {
    const mergeInfo = {};

    for (let idx = 0; idx < 7; idx++) {
      let startRowIndex = -1;
      let consecutiveCount = 0;

      for (let totalIndex = 0; totalIndex < data.length; totalIndex++) {
        const isNewTimeSlot = data[totalIndex][idx].isNeedNewDivide;

        // 새로운 시간대 시작이면 이전 병합 처리하고 초기화
        if (isNewTimeSlot && consecutiveCount > 0) {
          if (consecutiveCount > 1) {
            mergeInfo[`${startRowIndex}-${idx}`] = {
              shouldRender: true,
              mergedRowCount: consecutiveCount
            };

            for (let i = 1; i < consecutiveCount; i++) {
              mergeInfo[`${startRowIndex + i}-${idx}`] = { shouldRender: false };
            }
          }

          startRowIndex = -1;
          consecutiveCount = 0;
        }

        if (data[totalIndex][idx].className === '수업없음') {
          if (startRowIndex === -1) {
            startRowIndex = totalIndex;
          }

          consecutiveCount++;
        }
        else {
          if (consecutiveCount > 1) {
            mergeInfo[`${startRowIndex}-${idx}`] = {
              shouldRender: true,
              mergedRowCount: consecutiveCount
            };

            for (let i = 1; i < consecutiveCount; i++) {
              mergeInfo[`${startRowIndex + i}-${idx}`] = { shouldRender: false };
            }
          }

          startRowIndex = -1;
          consecutiveCount = 0;
        }
      }

      // 마지막 처리
      if (consecutiveCount > 1) {
        mergeInfo[`${startRowIndex}-${idx}`] = {
          shouldRender: true,
          mergedRowCount: consecutiveCount
        };

        for (let i = 1; i < consecutiveCount; i++) {
          mergeInfo[`${startRowIndex + i}-${idx}`] = { shouldRender: false };
        }
      }
    }

    return mergeInfo;
  }

  // 호버링 배경색 컬러 코드에서 RGB값으로 변경 //
  function hexToRGB(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    if (alpha) {
      return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ")";
    }
    else {
      return 'rgba(' + r + ', ' + g + ', ' + b + ")";
    }
  }

  // 비어있는칸 플러스 버튼 클릭시 //
  function onClickPlusColumnBox(outData, innerData) {
    let settingTime = '';

    for (let i = 0; i < outData.length; i++) {
      if (outData[i].classId !== -1 && outData[i].classId !== -2) {
        settingTime = outData[i].classTime;
        break;
      }
    }

    onClickPlusColumn(innerData.classDate, settingTime, moment(innerData.classDate, 'YYYY.MM.DD').format('ddd,'));
  }

  // 수업시간의 차이로 인한 윗쪽 빈칸 플러스 버튼 클릭시 //
  function onClickTopPlusColumnBox(outData, innerData) {
    const settingEndTime = innerData.classTime.split('-')[0];
    const originalClassTime = innerData.classTime;
    let settingStartTime = '';

    for (let i = 0; i < outData.length; i++) {
      if (outData[i].classId !== -1 && outData[i].classId !== -2 && outData[i].classTime !== originalClassTime) {
        settingStartTime = outData[i].classTime.split('-')[0];
        break;
      }
    }

    const settingTime = settingStartTime + '-' + settingEndTime;

    onClickPlusColumn(innerData.classDate, settingTime, moment(innerData.classDate, 'YYYY.MM.DD').format('ddd,'));
  }

  // 수업시간의 차이로 인한 아랫쪽 빈칸 플러스 버튼 클릭시 //
  function onClickBottomPlusColumnBox(outData, innerData) {
    const settingStartTime = innerData.classTime.split('-')[1];
    const originalClassTime = innerData.classTime;
    let settingEndTime = '';

    for (let i = 0; i < outData.length; i++) {
      if (outData[i].classId !== -1 && outData[i].classId !== -2 && outData[i].classTime !== originalClassTime) {
        settingEndTime = outData[i].classTime.split('-')[1];
        break;
      }
    }

    const settingTime = settingStartTime + '-' + settingEndTime;

    onClickPlusColumn(innerData.classDate, settingTime, moment(innerData.classDate, 'YYYY.MM.DD').format('ddd,'));
  }

  // 맨 위에 칸 플러스 버튼 클릭시 //
  function onClickFirstPlusColumnBox(innerData) {
    onClickPlusColumn(innerData.classDate, '', moment(innerData.classDate, 'YYYY.MM.DD').format('ddd,'));
  }

  // 수업 정보 컴포넌트 사이에 추가버튼을 띄울지 결정 //
  function selectBetweenButtonView(outerIndex, innerIndex) {
    // 마지막 컴포넌트는 버튼 추가 X //
    if (filteredTotalData.length === outerIndex + 1) {
      return true;
    }
    else {
      // 본 컴포넌트 아래에 수업없음이라 추가 컴포넌트가 존재하는 경우 //
      if (filteredTotalData[outerIndex + 1][innerIndex].className === '수업없음') {
        return false;
      }
      else {
        return true;
      }
    }
  }

  // 첫줄 추가 버튼 호버링 시작 시 //
  function onMouseEnterOfFirstButton(index) {
    // null 체크 추가
    if (ComponentRef.current[index]) {
      ComponentRef.current[index].style.transform = 'translate(0px, 6px)';
    }

    if (FirstButtonWrapperRef.current[index]) {
      FirstButtonWrapperRef.current[index].style.marginTop = `-14px`;
    }

    if (FirstButtonRef.current[index]) {
      FirstButtonRef.current[index].style.height = `18px`;
      FirstButtonRef.current[index].style.backgroundColor = `#FF8B48`;
      FirstButtonRef.current[index].style.opacity = '1';
    }
  }

  // 첫줄 추가 버튼 호버링 끝 //
  function onMouseLeaveOfFirstButton(index) {
    // null 체크 추가
    if (ComponentRef.current[index]) {
      ComponentRef.current[index].style.transform = 'translate(0px, 0px)';
    }

    if (FirstButtonWrapperRef.current[index]) {
      FirstButtonWrapperRef.current[index].style.marginTop = `-8px`;
    }

    if (FirstButtonRef.current[index]) {
      FirstButtonRef.current[index].style.height = `8px`;
      FirstButtonRef.current[index].style.backgroundColor = `#FFFFFF`;
      FirstButtonRef.current[index].style.opacity = '0';
    }
  }

  // 마우스 호버링 시작시 //
  function onMouseEnter(outerIndex, innerIndex, topMargin) {
    const currentIndex = (outerIndex * 7) + innerIndex;
    const nextIndex = ((outerIndex + 1) * 7) + innerIndex;

    // null 체크 추가
    if (ComponentRef.current[currentIndex]) {
      ComponentRef.current[currentIndex].style.transform = 'translate(0px, -6px)';
    }

    if (filteredTotalData.length > (outerIndex + 1) && ComponentRef.current[nextIndex]) {
      ComponentRef.current[nextIndex].style.transform = 'translate(0px, 6px)';
    }

    if (ButtonWrapperRef.current[currentIndex]) {
      ButtonWrapperRef.current[currentIndex].style.marginTop = (topMargin - 5) + 'px';
    }

    if (ButtonRef.current[currentIndex]) {
      ButtonRef.current[currentIndex].style.height = `18px`;
      ButtonRef.current[currentIndex].style.backgroundColor = `#FF8B48`;
      ButtonRef.current[currentIndex].style.opacity = '1';
    }
  }

  // 마우스 호버링 끝날시 //
  function onMouseLeave(outerIndex, innerIndex, topMargin) {
    const currentIndex = (outerIndex * 7) + innerIndex;
    const nextIndex = ((outerIndex + 1) * 7) + innerIndex;

    // null 체크 추가
    if (ComponentRef.current[currentIndex]) {
      ComponentRef.current[currentIndex].style.transform = 'translate(0px, 0px)';
    }

    if (filteredTotalData.length > (outerIndex + 1) && ComponentRef.current[nextIndex]) {
      ComponentRef.current[nextIndex].style.transform = 'translate(0px, 0px)';
    }

    if (ButtonWrapperRef.current[currentIndex]) {
      ButtonWrapperRef.current[currentIndex].style.marginTop = (topMargin + 5) + 'px';
    }

    if (ButtonRef.current[currentIndex]) {
      ButtonRef.current[currentIndex].style.height = `8px`;
      ButtonRef.current[currentIndex].style.backgroundColor = `#FFFFFF`;
      ButtonRef.current[currentIndex].style.opacity = '0';
    }
  }

  // 주간 데이터 클릭시
  function onClickWeeklyScheduleBox(data) {
    if (data.className !== '브레이크 타임' && data.className !== '수업없음') {
      setClickDate(data.classDate);
      onClickScheduleDetail(data.scheduleId, data);
    }
  }

  // 수업 삭제 버튼 클릭시 //
  function onClickDeletedClass(data) {
    onClickDeleteButton(data);
  }

  // 수업 시간에 따른 수업 추가 버튼 크기 계산 //
  function onCalcNoneTDHeight(data) {
    let finalStartTime = 0;
    let finalEndTime = 0;

    for (let i = 0; i < data.length; i++) {
      if (data[i].className !== '수업없음') {
        const { startValue, endValue } = parseClassTimeRange(data[i].classTime);

        if (finalStartTime === 0 && finalEndTime === 0) {
          finalStartTime = startValue;
          finalEndTime = endValue;
        }
        else {
          if (finalStartTime > startValue) {
            finalStartTime = startValue;
          }
          if (finalEndTime < endValue) {
            finalEndTime = endValue;
          }
        }
      }
    }

    const finalRate = finalEndTime - finalStartTime;

    if (finalRate < 1.5) {
      return HEIGHT_CONSTANTS.SCHEDULE_BOX_BASE_HEIGHT;
    }
    else {
      return ((((finalRate - 1) * conversionRate) + 1) * HEIGHT_CONSTANTS.SCHEDULE_BOX_BASE_HEIGHT);
    }
  }

  // 병합된 수업없음 셀의 높이 계산 //
  function onCalcMergedNoneTDHeight(startRowIndex, mergedRowCount, data) {
    let totalHeight = 0;

    for (let i = 0; i < mergedRowCount; i++) {
      const rowIndex = startRowIndex + i;

      // 범위 체크 및 유효성 검증
      if (rowIndex >= data.length || !data[rowIndex] || !Array.isArray(data[rowIndex])) {
        console.warn('Invalid row at index', rowIndex, 'data length:', data.length);

        break;
      }

      totalHeight += onCalcNoneTDHeight(data[rowIndex]);

      if (i < mergedRowCount - 1) {
        totalHeight += HEIGHT_CONSTANTS.ROW_GAP; // 행 간 gap 추가
      }
    }

    return totalHeight;
  }

  // 수업 시간에 따른 크기 변환 계산 //
  function onCalcTDHeight(data) {
    const { startValue, endValue } = parseClassTimeRange(data.classTime);
    const finalRate = endValue - startValue;

    if (finalRate < 1.5) {
      return HEIGHT_CONSTANTS.SCHEDULE_BOX_BASE_HEIGHT;
    }
    else {
      return (((finalRate - 1) * conversionRate) + 1) * HEIGHT_CONSTANTS.SCHEDULE_BOX_BASE_HEIGHT;
    }
  }

  // 같은 시간대에 있는 수업이 너무 긴경우 아랫쪽에 수업 추가 버튼을 생성할지 말지 결정 //
  function onCalcUpAdditionalButtonHeight(weeklyData, data) {
    let minimumStartTime = 0;

    for (let i = 0; i < weeklyData.length; i++) {
      if (weeklyData[i].className !== '수업없음') {
        const { startValue } = parseClassTimeRange(weeklyData[i].classTime);

        if (minimumStartTime === 0) {
          minimumStartTime = startValue;
        }
        else {
          if (minimumStartTime > startValue) {
            minimumStartTime = startValue;
          }
        }
      }
    }

    const { startValue } = parseClassTimeRange(data.classTime);
    const finalRate = startValue - minimumStartTime;

    if ((finalRate) === 0) {
      return 0;
    }
    else if (finalRate < 1) {
      return 0;
    }
    // row gap 및 marginBottom 보정 //
    else {
      return ((finalRate * conversionRate) * HEIGHT_CONSTANTS.SCHEDULE_BOX_BASE_HEIGHT)
        - HEIGHT_CONSTANTS.ROW_GAP
        - HEIGHT_CONSTANTS.SCHEDULE_BOX_GAP;
    }
  }

  // 같은 시간대에 있는 수업이 너무 긴경우 아랫쪽에 수업 추가 버튼을 생성할지 말지 결정 //
  function onCalcDownAdditionalButtonHeight(weeklyData, data) {
    // 같은 행에서 가장 긴 셀의 높이 찾기
    let maxCellHeight = 0;

    for (let i = 0; i < weeklyData.length; i++) {
      if (weeklyData[i].className !== '수업없음') {
        const cellHeight = onCalcTDHeight(weeklyData[i]);

        if (cellHeight > maxCellHeight) {
          maxCellHeight = cellHeight;
        }
      }
    }

    // 현재 셀의 높이
    const currentCellHeight = onCalcTDHeight(data);

    // 높이 차이 계산 (스케줄 박스와 + 박스 사이 갭 보정)
    const heightDifference = maxCellHeight - currentCellHeight - HEIGHT_CONSTANTS.SCHEDULE_BOX_GAP;

    if (heightDifference <= 0) {
      return 0;
    }

    return heightDifference;
  }

  // 같은 시간대의 행 개수 계산 //
  function calculateRowCount(index) {
    let rowCount = 1;
    for (let i = index + 1; i < filteredTotalData.length; i++) {
      if (filteredTotalData[i][0].isNeedNewDivide) break;

      rowCount++;
    }
    return rowCount;
  }

  // 셀 병합 정보 가져오기 //
  function getCellInfo(totalIndex, dayIdx) {
    const key = `${totalIndex}-${dayIdx}`;
    
    return mergeInfo[key] || { shouldRender: true, mergedRowCount: 1 };
  }

  // 주간 삭제 버튼 클릭시 //
  function onClickWeeklyDeleteButton(data, index) {
    const deletedScheduleIds = [];
    const deletedBreakTimeIds = [];

    for (let i = 0; i < totalData.length; i++) {
      if (totalData[i][index].classId === -1) deletedBreakTimeIds.push(totalData[i][index].breakTimeId);
      else if (totalData[i][index].classId !== -2) deletedScheduleIds.push(totalData[i][index].scheduleId);
    }

    setDeletedWeek({
      scheduleIds: deletedScheduleIds,
      breakTimeIds: deletedBreakTimeIds,
      changeApplyWeek: data.format('ddd,'),
      deletedDate: data.format('YYYY-MM-DDTHH:mm:ss')
    });

    setIsClickedWeekDelete(true);
  }

  // 주간 삭제 취소 클릭시 //
  function onClickExitWeeklyDeleteButton() {
    if (isInProgress) {
      alert('삭제가 진행중입니다');
      return;
    }

    setDeletedWeek(undefined);
    setIsClickedWeekDelete(false);
  }

  // 수업 내역 삭제모달 안에서 삭제 클릭시 //
  function onClickOnlyOneDeleteWeek() {

    if (isInProgress) {
      alert('삭제가 진행중입니다');

      return;
    }
    else if (deletedWeek === undefined) {
      alert('삭제할 수업 일정을 선택해주세요');

      return;
    }

    setIsInProgress(true);

    const requestData = {
      scheduleIds: deletedWeek.scheduleIds,
      breakTimeIds: deletedWeek.breakTimeIds,
      changeApplyWeek: deletedWeek.changeApplyWeek,
      deletedDate: moment(deletedWeek.deletedDate).format('YYYY-MM-DDT00:00:00')
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('schedule/delete/week/only_one', 'POST', requestData);

        if (responseData.status === 200) {
          setDeletedWeek(undefined);
          setIsClickedWeekDelete(false);
          setRendering(!rendering);
          setIsInProgress(false);
        }
        else {
          alert('삭제에 실패하였습니다.\n잠시 후 다시시도해주세요');
          setIsClickedWeekDelete(false);
          setIsInProgress(false);
        }
      }
    )();
  }

  // 수업 내역 삭제모달 안에서 삭제 클릭시 //
  function onClickDeleteWeek() {

    if (isInProgress) {
      alert('삭제가 진행중입니다');
      return;
    }
    else if (deletedWeek === undefined) {
      alert('삭제할 수업 일정을 선택해주세요');
      return;
    }

    setIsInProgress(true);

    const requestData = {
      scheduleIds: deletedWeek.scheduleIds,
      breakTimeIds: deletedWeek.breakTimeIds,
      changeApplyWeek: deletedWeek.changeApplyWeek,
      deletedDate: moment(deletedWeek.deletedDate).format('YYYY-MM-DDT00:00:00')
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('schedule/delete/week', 'POST', requestData);

        if (responseData.status === 200) {
          setDeletedWeek(undefined);
          setIsClickedWeekDelete(false);
          setRendering(!rendering);
          setIsInProgress(false);
        }
        else {
          alert('삭제에 실패하였습니다.\n잠시 후 다시시도해주세요');
          setIsClickedWeekDelete(false);
          setIsInProgress(false);
        }
      }
    )();
  }

  // 시간 구분선 렌더링 함수 //
  function renderTimeDivisionLines() {
    return filteredTotalData.map((data, index) => {
      if (!data[0].isNeedNewDivide) return null;

      return (
        <NewTimeDivisionLine
          key={'NewTimeDivisionLine-' + index}
          index={index}
          conversionRate={conversionRate}
          totalData={filteredTotalData}
          rowCount={calculateRowCount(index)}
          mergeInfo={mergeInfo} />
      );
    });
  }

  // 수업없음 셀 렌더링 함수 //
  function renderEmptyCell(data, innerData, totalIndex, dayIdx, cellInfo) {
    const height = cellInfo.mergedRowCount > 1 ?
      onCalcMergedNoneTDHeight(totalIndex, cellInfo.mergedRowCount, filteredTotalData)
      :
      onCalcNoneTDHeight(data);

    return (
      <ComponentWrapper key={'Class-' + totalIndex}>
        <TDWrapper ref={(element) => ComponentRef.current[(totalIndex * 7) + dayIdx] = element}>
          <NoneClassTd
            height={height}
            backgroundColor={hexToRGB('#ECECEC', 0.8)}
            onClick={() => onClickPlusColumnBox(data, innerData)}>
            <Plus src={PlusPath} />
          </NoneClassTd>
        </TDWrapper>
        <BetweenEmptyBox ref={(element) => ButtonRef.current[(totalIndex * 7) + dayIdx] = element} />
      </ComponentWrapper>
    );
  }

  // 수업 셀 렌더링 함수 //
  function renderClassCell(data, innerData, totalIndex, dayIdx) {
    const upHeight = onCalcUpAdditionalButtonHeight(data, innerData);
    const downHeight = onCalcDownAdditionalButtonHeight(data, innerData);
    const showBetweenButton = downHeight === 0 && selectBetweenButtonView(totalIndex, dayIdx);

    return (
      <ComponentWrapper key={'Class-' + totalIndex}>
        {
          totalIndex === 0 &&
          <FirstPlusButtonWrapper
            ref={(element) => FirstButtonWrapperRef.current[dayIdx] = element}
            onMouseEnter={() => onMouseEnterOfFirstButton(dayIdx)}
            onMouseLeave={() => onMouseLeaveOfFirstButton(dayIdx)}>
            <FirstPlusButton
              ref={(element) => FirstButtonRef.current[dayIdx] = element}
              onClick={() => onClickFirstPlusColumnBox(innerData)}>
              <BetweenPlusWidth />
              <BetweenPlusHeight />
            </FirstPlusButton>
          </FirstPlusButtonWrapper>
        }
        {
          upHeight !== 0 &&
          <TDWrapper>
            <NoneClassTd
              style={{ marginBottom: '6px' }}
              height={upHeight}
              backgroundColor={hexToRGB('#ECECEC', 0.8)}
              onClick={() => onClickTopPlusColumnBox(data, innerData)}>
              <Plus src={PlusPath} />
            </NoneClassTd>
          </TDWrapper>
        }
        <TDWrapper ref={(element) => ComponentRef.current[(totalIndex * 7) + dayIdx] = element}>
          <ScheduleClassBox
            totalIndex={totalIndex}
            idx={dayIdx}
            data={innerData}
            canceledSchedules={canceledSchedules}
            conversionRate={conversionRate}
            onClickWeeklyScheduleBox={onClickWeeklyScheduleBox}
            onClickDeletedClass={onClickDeletedClass} />
        </TDWrapper>
        {
          downHeight !== 0 &&
          <TDWrapper>
            <NoneClassTd
              style={{ marginTop: '6px' }}
              height={downHeight}
              backgroundColor={hexToRGB('#ECECEC', 0.8)}
              onClick={() => onClickBottomPlusColumnBox(data, innerData)}>
              <Plus src={PlusPath} />
            </NoneClassTd>
          </TDWrapper>
        }
        {
          showBetweenButton ?
            <BetweenPlusButtonWrapper
              ref={(element) => ButtonWrapperRef.current[(totalIndex * 7) + dayIdx] = element}
              marginTop={onCalcTDHeight(innerData)}
              onMouseEnter={() => onMouseEnter(totalIndex, dayIdx, onCalcTDHeight(innerData))}
              onMouseLeave={() => onMouseLeave(totalIndex, dayIdx)}>
              <BetweenPlusButton
                ref={(element) => ButtonRef.current[(totalIndex * 7) + dayIdx] = element}
                onClick={() => onClickPlusColumnBox(data, innerData)}>
                <BetweenPlusWrapper>
                  <BetweenPlusWidth />
                  <BetweenPlusHeight />
                </BetweenPlusWrapper>
              </BetweenPlusButton>
            </BetweenPlusButtonWrapper>
            :
            <BetweenEmptyBox />
        }
      </ComponentWrapper>
    );
  }

  // 스케줄 셀 렌더링 함수 (수업없음 / 수업 분기) //
  function renderScheduleCell(data, totalIndex, dayIdx) {
    const innerData = data[dayIdx];
    const cellInfo = getCellInfo(totalIndex, dayIdx);

    if (cellInfo.shouldRender === false) return null;

    if (innerData.className === '수업없음') {
      return renderEmptyCell(data, innerData, totalIndex, dayIdx, cellInfo);
    }
    else {
      return renderClassCell(data, innerData, totalIndex, dayIdx);
    }
  }

  // 요일별 컬럼 렌더링 함수 //
  function renderDayColumns() {
    return DAYS_OF_WEEK.map((dayIdx) => (
      <DayColumn key={'Day-' + dayIdx}>
        {filteredTotalData.map((data, totalIndex) => renderScheduleCell(data, totalIndex, dayIdx))}
      </DayColumn>
    ));
  }

  return (
    <TopLevelWrapper ref={TopLevelRef}>
      {
        isClickedWeekDelete &&
        <WeekClassDeleteModal
          deletedData={deletedWeek}
          onClickExit={onClickExitWeeklyDeleteButton}
          onClickOnlyOneDelete={onClickOnlyOneDeleteWeek}
          onClickAllDelete={onClickDeleteWeek} />
      }
      <WeeklyColumn
        isLoading={isLoading}
        height={weekAllDeleteBackgroundHeight.height}
        today={startDay}
        standardDate={today}
        onClickWeeklyDeleteButton={onClickWeeklyDeleteButton} />
      {
        isLoading ?
          <LoadingWrapper minHeight={minHeight}>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </LoadingWrapper>
          :
          <ColumnWrapper ref={ColumnRef}>
            {
              filteredTotalData.length !== 0 ?
                <TableWrapper>
                  <TimeDivisionLineWrapper>
                    {renderTimeDivisionLines()}
                  </TimeDivisionLineWrapper>
                  <Table>
                    <Tbody>
                      <Tr>
                        {renderDayColumns()}
                      </Tr>
                    </Tbody>
                  </Table>
                </TableWrapper>
                :
                <EmptyClassInfoBox>
                  <EmptyImage src={EmptySign} />
                  <EmptyText>수업이 존재하지 않습니다</EmptyText>
                </EmptyClassInfoBox>
            }
          </ColumnWrapper>
      }
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  // margin-top: 28px;
`;

const ColumnWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  z-index: 6;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 974px;
  min-height: ${props => props.minHeight}px;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;
  
  // width: 974px;
  width: 994px;
  height: auto;
  
  gap: 10px;
`;

const TimeDivisionLineWrapper = styled.div`
	display: flex;
	flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  min-width: 70px;
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: calc(100% - 80px);
  // width: 100%;
`;

const Tbody = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  gap: 8px;
`;

const Tr = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-start;

  width: 911px;

  gap: 8px;
  margin: 0 0 0 0;
`;

const DayColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  gap: 8px;
`;

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  transition: all 0.3s ease-in-out;
`;

const TDWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  transition: all 0.1s ease-in-out;
`;

const BetweenEmptyBox = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: 68px;

  width: 50px;
  height: 8px;

  opacity: 0;
  background-color: #FFFFFF;
`;

const BetweenPlusButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: ${props => props.marginTop}px;

  width: 125px;

  &:hover {
    cursor: pointer;
  }

  transition: all 0.1s ease-in-out;
`;

const BetweenPlusButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 50px;
  height: 8px;

  opacity: 0;

  border-radius: 4px;
  background-color: #FFFFFF;

  transition: all 0.1s ease-in-out;
`;

const BetweenPlusWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 12px;
  height: 100%;
`;

const BetweenPlusWidth = styled.div`
  position: absolute;

  width: 7px;
  height: 1px;

  border-radius: 3px;
  background-color: #FFFFFF;
`;

const BetweenPlusHeight = styled.div`
  position: absolute;

  width: 1px;
  height: 7px;

  border-radius: 3px;
  background-color: #FFFFFF;
`;

const FirstPlusButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: -8px;

  width: 125px;

  &:hover {
    cursor: pointer;
  }
  
  transition: all 0.1s ease-in-out;
`;

const FirstPlusButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 50px;
  height: 8px;

  opacity: 0;

  border-radius: 4px;
  background-color: #FFFFFF;

  transition: all 0.1s ease-in-out;
`;

const NoneClassTd = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  // width: 117px;
  width: 121px;
  height: ${props => props.height}px;

  // border: 4px solid #FFFFFF;
  border-radius: 4px;
  background-color: ${props => props.backgroundColor};
  
  overflow: hidden;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      // border: 4px solid #FF8B48;
      background-color: #FF8B48;
    }
  }
`;

const Plus = styled.img`
  width: 30px;
  height: 30px;
`;

const EmptyClassInfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 974px;
  height: 644px;
`;

const EmptyImage = styled.img`
  width: 148px;
  height: 62px;
`;

const EmptyText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: #CBCBCB;

  margin: 8px 0 0 0;

  &:hover {
    cursor: default;
  }
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 60px;
  height: 60px;
`;